import React, { useState, useEffect } from 'react';
import { Edit3, Save, RotateCcw, Image as ImageIcon, Upload, Trash2, Crown } from 'lucide-react';
import { garageInfo } from '../mock/mockData';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { carsysApi } from '../api/carsysApi';
import { confirmDialog } from '../lib/confirm';
import { toast } from 'sonner';
import InvoiceDocument from '../components/InvoiceDocument';

import { useT } from '../context/LanguageContext';

function LogoImage({ src }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    const tok = localStorage.getItem('monteurcentraal_token');
    const url = src.split('?')[0];
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(b => { if (!cancelled) setBlobUrl(URL.createObjectURL(b)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [src]);
  if (!blobUrl) return null;
  return <img src={blobUrl} alt={("Logo","Logo")} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} data-testid="layout-logo-img" />;
}

const DEFAULT_LAYOUT = { titel: 'FACTUUR', kleur: '#2563eb', voettekst: '', logoTekst: '' };

const _today = new Date().toISOString().slice(0, 10);
const SAMPLE_KLANT = { naam: 'Jan de Vries', adres: 'Kerkstraat 5', postcode: '1011 AA', plaats: 'Amsterdam', email: 'jan@voorbeeld.nl', telefoon: '06-12345678' };
const SAMPLE_VOERTUIG = { kenteken: '35-JZ-VK', merk: 'Volkswagen', model: 'Polo', uitvoering: '1.4 TDI', bouwjaar: 2016, kmStand: 184225, chassisnummer: 'WVWZZZ6RZGY123456', apkVervalt: '2026-09-12' };
const SAMPLE_BON = {
  type: 'Factuur', nummer: 'F-2026-0142', datum: _today, kenteken: '35-JZ-VK', kilometerstand: 184225,
  werkorderNummer: 'W-2026-0006', monteur: 'Leon Kuipers', werkdatum: _today, referentie: '',
  regels: [
    { type: 'Arbeid', code: 'ARB', omschr: 'Diagnose / uitlezen', aantal: 1, prijs: 45, markering: 'Klant' },
    { type: 'Arbeid', code: 'ARB', omschr: 'Montage remblokken voor', aantal: 2, prijs: 55, markering: 'Klant' },
    { type: 'Onderdeel', code: 'OF-1042', omschr: 'Oliefilter', aantal: 1, prijs: 18.5, markering: 'Klant' },
    { type: 'Onderdeel', code: '5W30', omschr: 'Motorolie 5W30', aantal: 5, prijs: 12.9, markering: 'Klant' },
  ],
  subtotaal: 238, btw: 49.98, totaal: 287.98, betaald: 0, openstaand: 287.98,
  aanbevolenOnderhoud: 'Distributieriem vervangen bij 200.000 km',
};

export default function BonLayout() {
  const { t } = useT();
  const { gegevens, refreshAll } = useData();
  const { user } = useAuth();
  const info = (gegevens && gegevens.naam) ? gegevens : garageInfo;
  const [titel, setTitel] = useState(DEFAULT_LAYOUT.titel);
  const [kleur, setKleur] = useState(DEFAULT_LAYOUT.kleur);
  const [voettekst, setVoettekst] = useState(DEFAULT_LAYOUT.voettekst);
  const [logo, setLogo] = useState('');
  const [saving, setSaving] = useState(false);
  const [logoCacheBust, setLogoCacheBust] = useState(0);
  const [cloudStatus, setCloudStatus] = useState({ plan: 'NONE', planLimits: { cloud: false } });
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);

  useEffect(() => { carsysApi.getCloudStatus().then(setCloudStatus).catch(() => {}); }, []);

  // Laad opgeslagen layout uit gegevens
  useEffect(() => {
    const bl = gegevens?.bonLayout;
    if (bl) {
      setTitel(bl.titel ?? DEFAULT_LAYOUT.titel);
      setKleur(bl.kleur ?? DEFAULT_LAYOUT.kleur);
      setVoettekst(bl.voettekst ?? t('Bedankt voor uw vertrouwen in onze garage!','Bedankt voor uw vertrouwen in onze garage!'));
      setLogo(bl.logoTekst ?? '');
    }
  }, [gegevens?.bonLayout]);

  const handleSaveLayout = async () => {
    setSaving(true);
    try {
      await carsysApi.updateGegevens({ bonLayout: { titel, kleur, voettekst, logoTekst: logo } });
      await refreshAll();
      toast.success(t('Layout opgeslagen'));
    } catch (err) {
      toast.error(t('Opslaan mislukt'), { description: err.response?.data?.detail || err.message });
    } finally { setSaving(false); }
  };

  const handleResetLayout = async () => {
    setTitel(DEFAULT_LAYOUT.titel); setKleur(DEFAULT_LAYOUT.kleur);
    setVoettekst(DEFAULT_LAYOUT.voettekst); setLogo('');
    try {
      await carsysApi.updateGegevens({ bonLayout: { ...DEFAULT_LAYOUT } });
      await refreshAll();
      toast.success(t('Standaard hersteld'));
    } catch (err) {
      toast.error(t('Opslaan mislukt'), { description: err.response?.data?.detail || err.message });
    }
  };

  const proPlan = !!cloudStatus.planLimits?.cloud;
  const hasLogo = !!gegevens?.logoPath;
  const logoSrc = hasLogo && user?.garageId
    ? `${carsysApi.logoUrl(user.garageId)}?t=${logoCacheBust || 1}`
    : null;

  // Logo als blob-URL voor de Live preview (zelfde als op de echte factuur)
  useEffect(() => {
    if (!hasLogo || !user?.garageId) { setLogoBlobUrl(null); return; }
    let cancelled = false;
    const tok = localStorage.getItem('monteurcentraal_token');
    fetch(carsysApi.logoUrl(user.garageId), { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(b => { if (!cancelled) setLogoBlobUrl(URL.createObjectURL(b)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [hasLogo, user?.garageId, logoCacheBust]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t('Logo te groot (max 2 MB)')); return; }
    try {
      await carsysApi.uploadLogo(file);
      toast.success(t('Logo geüpload'));
      await refreshAll();
      setLogoCacheBust(Date.now());
    } catch (err) {
      toast.error(t('Upload mislukt'), { description: err.response?.data?.detail || err.message });
    } finally {
      e.target.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!(await confirmDialog(t('Logo verwijderen?')))) return;
    try {
      await carsysApi.deleteLogo();
      toast.success(t('Logo verwijderd'));
      await refreshAll();
      setLogoCacheBust(Date.now());
    } catch (err) {
      toast.error(t('Verwijderen mislukt'), { description: err.response?.data?.detail || err.message });
    }
  };

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('page.layout.title')}</h2><p>{t('page.layout.sub')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cs-btn" onClick={handleResetLayout} data-testid="btn-layout-reset"><RotateCcw size={14} /> {t('Reset')}</button>
          <button className="cs-btn primary" onClick={handleSaveLayout} disabled={saving} data-testid="btn-layout-save"><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>

      {/* LOGO UPLOAD CARD */}
      <div className="cs-card padded" style={{ marginBottom: 14 }} data-testid="card-logo-bonlayout">
        <h3 className="cs-card-title">
          <ImageIcon size={16} /> {t('Bedrijfslogo op factuur', 'Bedrijfslogo op factuur')}
          {!proPlan && <span className="cs-badge blue" style={{ marginLeft: 8, fontSize: 10 }}><Crown size={10} /> PRO/Enterprise</span>}
        </h3>
        {!proPlan ? (
          <div style={{ padding: 14, background: 'var(--cs-amber-soft)', borderRadius: 8, color: 'var(--cs-amber)', fontSize: 13 }}>
            {t('Logo op de factuur is beschikbaar in PRO en ENTERPRISE.', 'Logo op de factuur is beschikbaar in PRO en ENTERPRISE.')} {t('Activeer een licentie via Instellingen → Plannen.', 'Activeer een licentie via Instellingen → Plannen.')}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <div style={{ width: 160, height: 100, border: '1px dashed var(--cs-border)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafbfc', overflow: 'hidden' }}>
              {hasLogo ? <LogoImage src={logoSrc} /> : <span style={{ fontSize: 11, color: 'var(--cs-text-muted)' }}>{t('Geen logo', 'Geen logo')}</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label className="cs-btn primary" style={{ cursor: 'pointer' }} data-testid="btn-upload-logo-bonlayout">
                <Upload size={14} /> {hasLogo ? t('Logo vervangen', 'Logo vervangen') : t('Logo uploaden', 'Logo uploaden')}
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} onChange={handleLogoUpload} />
              </label>
              {hasLogo && (
                <button className="cs-btn danger" onClick={handleLogoDelete} data-testid="btn-delete-logo-bonlayout">
                  <Trash2 size={14} /> {t('Logo verwijderen', 'Logo verwijderen')}
                </button>
              )}
              <span style={{ fontSize: 11, color: 'var(--cs-text-muted)' }}>{t('PNG, JPG, SVG, WebP · max 2 MB', 'PNG, JPG, SVG, WebP · max 2 MB')}</span>
            </div>
          </div>
        )}
      </div>

      <div className="cs-two-col">
        <div className="cs-card padded">
          <h3 className="cs-card-title"><Edit3 size={16} /> {t('Editor', 'Editor')}</h3>
          <div className="cs-detail-row"><span className="lbl">{t('Logo tekst', 'Logo tekst')}</span><input className="cs-input" value={logo} onChange={e => setLogo(e.target.value)} /></div>
          <div className="cs-detail-row"><span className="lbl">{t('Documenttitel', 'Documenttitel')}</span><input className="cs-input" value={titel} onChange={e => setTitel(e.target.value)} /></div>
          <div className="cs-detail-row"><span className="lbl">{t('Accentkleur', 'Accentkleur')}</span><input type="color" value={kleur} onChange={e => setKleur(e.target.value)} /></div>
          <div className="cs-detail-row"><span className="lbl">{t('Voettekst', 'Voettekst')}</span><textarea className="cs-input" rows={3} value={voettekst} onChange={e => setVoettekst(e.target.value)} /></div>
        </div>

        <div className="cs-card padded">
          <h3 className="cs-card-title">{t('Live preview', 'Live preview')} <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--cs-text-muted)' }}>· {t('exact zoals de PDF', 'exact zoals de PDF')}</span></h3>
          <div style={{ background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 6, padding: 20, overflowX: 'auto' }}>
            <InvoiceDocument
              bon={SAMPLE_BON}
              klant={SAMPLE_KLANT}
              voertuig={SAMPLE_VOERTUIG}
              gegevens={{ ...info, bonLayout: { titel, kleur, voettekst, logoTekst: logo } }}
              layout={{ titel, kleur, voettekst, logoTekst: logo }}
              logoUrl={logoBlobUrl}
              t={t}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
